import type { ApolloError, FetchResult } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import type { MutationResult } from '@livecontrol/scheduler/store';
import { Errors } from '@livecontrol/scheduler/store';
import { useCallback, useState } from 'react';

type Args = Partial<{
  banner: string | null;
  redirectURL: string | null;
  highPriority: boolean | null;
  highPriorityNote: string | null;
  liveChat: boolean | null;
}> & {
  id: number;
};

interface TVariables {
  event: {
    id: number;
    banner?: string | null;
    redirect_url?: string | null;
    high_priority?: boolean | null;
    high_priority_note?: string | null;
    live_chat?: boolean | null;
  };
}

interface TData {
  updateEventAdminFields?: {
    banner: string | null;
    redirect_url: string | null;
    high_priority: boolean | null;
    high_priority_note: string | null;
    live_chat: boolean | null;
  };
}

const MUTATION = gql`
  mutation UpdateEventAdminFields($event: UpdateEventAdminFieldsInput!) {
    updateEventAdminFields(event: $event) {
      banner    
      redirect_url
      high_priority
      high_priority_note
      live_chat
    }
  }
`;

export const useUpdateEventAdminFields = (): [
  (args: Args) => Promise<TData['updateEventAdminFields'] | undefined>,
  MutationResult<TData['updateEventAdminFields'], 'event'>
] => {
  const [mutation, result] = useMutation<TData, TVariables>(MUTATION);
  const [error, setError] = useState<Error | undefined>();
  const [event, setEvent] = useState<TData['updateEventAdminFields'] | undefined>();

  return [
    useCallback(
      async (args: Args): Promise<TData['updateEventAdminFields'] | undefined> => {
        let event_: TData['updateEventAdminFields'] | undefined;

        setError(undefined);

        try {
          const { id, banner, redirectURL, highPriority, highPriorityNote, liveChat } = args;

          if (!id) {
            throw Errors.badRequest();
          }

          // Construir las variables solo con los campos que se proporcionan
          const eventInput: TVariables['event'] = { id };

          // Solo agregar campos que están definidos (incluso si son null para vaciarlos)
          if (banner !== undefined) {
            eventInput.banner = banner;
          }

          if (redirectURL !== undefined) {
            eventInput.redirect_url = redirectURL;
          }

          if (highPriority !== undefined) {
            eventInput.high_priority = highPriority;
          }

          if (highPriorityNote !== undefined) {
            eventInput.high_priority_note = highPriorityNote;
          }

          if (liveChat !== undefined) {
            eventInput.live_chat = liveChat;
          }

          const variables: TVariables = {
            event: eventInput
          };

          // Execute the GraphQL mutation
          const response = await mutation({ variables })
            .then(({ data }: FetchResult<TData>) => data?.updateEventAdminFields)
            .catch((_error: ApolloError) => {
              throw new Error('Server error'); // Reemplaza con tu Errors.serverError()
            });

          // Save the server response
          event_ = response;
        } catch (error_: unknown) {
          setError(<Error>error_);
        }

        setEvent(event_);

        return event_;
      },
      [mutation]
    ),
    {
      event,
      error,
      called: result.called,
      loading: result.loading
    }
  ];
};
